import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { UserRole } from "@/lib/supabase";
import {
  hasRole as hasRoleHelper,
  isAonInternal as isAonInternalHelper,
  primaryRole,
} from "./roles";
import { clearMfaEnrollmentCache } from "./mfa-enrollment";
import { useImpersonation } from "./ImpersonationContext";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /**
   * ID utilisateur "effectif" à utiliser pour charger les données :
   * - en mode normal : l'ID de l'utilisateur connecté
   * - en mode impersonation : l'ID de l'utilisateur impersoné
   * Toujours préférer ce champ à `user.id` dans les hooks data côté salarié.
   */
  effectiveUserId: string | null;
  /** True si l'admin est actuellement en train d'impersoner un autre utilisateur. */
  isImpersonating: boolean;
  /** Rôles applicatifs portés par l'utilisateur courant (peut être vide). */
  roles: UserRole[];
  /** Rôle "principal" (le plus privilégié) — utile pour l'affichage par défaut. */
  role: UserRole | null;
  /** L'utilisateur porte-t-il un rôle interne Aon (consultant, chef projet, admin) ? */
  isAonInternal: boolean;
  /** Helper synchrone pour tester un rôle précis. */
  hasRole: (role: UserRole) => boolean;
  /** Recharge les rôles depuis Supabase (utile après une mutation côté admin). */
  refreshRoles: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const { impersonated, stopImpersonating } = useImpersonation();

  const loadRoles = useCallback(async (userId: string | null) => {
    if (!userId) {
      setRoles([]);
      return;
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .returns<Array<{ role: UserRole }>>();
    if (error) {
      console.error("[auth] Failed to load user roles", error);
      setRoles([]);
      return;
    }
    setRoles((data ?? []).map((r) => r.role));
  }, []);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      clearMfaEnrollmentCache(data.session?.user.id);
      setSession(data.session);
      await loadRoles(data.session?.user.id ?? null);
      if (!mounted) return;
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      clearMfaEnrollmentCache(
        _event === "MFA_CHALLENGE_VERIFIED" ? undefined : nextSession?.user.id,
      );
      setSession(nextSession);
      void loadRoles(nextSession?.user.id ?? null);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [loadRoles]);

  const refreshRoles = useCallback(async () => {
    await loadRoles(session?.user.id ?? null);
  }, [loadRoles, session]);

  // En impersonation RÉELLE, la session Supabase est déjà celle de la cible :
  // les rôles chargés depuis `user_roles` sont donc bien ceux du salarié.
  const visibleRoles = roles;

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      effectiveUserId: session?.user.id ?? impersonated?.id ?? null,
      isImpersonating: Boolean(impersonated),
      roles: visibleRoles,
      role: primaryRole(visibleRoles),
      isAonInternal: isAonInternalHelper(visibleRoles),
      hasRole: (r: UserRole) => hasRoleHelper(visibleRoles, r),
      refreshRoles,
      signOut: async () => {
        clearMfaEnrollmentCache();
        if (impersonated) return stopImpersonating();
        await supabase.auth.signOut();
      },
    }),
    [session, loading, visibleRoles, refreshRoles, impersonated, stopImpersonating],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
